import React, { useMemo } from 'react';
import { distance, fmtDistance } from '../lib/geo';

/* Simple SVG line chart of altitude (m) vs. cumulative distance (m).
   Returns null when no points have altitude data. */
export default function ElevationChart({ points = [], height = 140 }) {
  const data = useMemo(() => {
    if (!points || points.length < 2) return null;
    let cum = 0;
    const rows = [];
    let hasAlt = false;
    for (let i = 0; i < points.length; i++) {
      if (i > 0) cum += distance(points[i - 1], points[i]);
      const a = points[i].altitude;
      if (a != null && !isNaN(a)) hasAlt = true;
      rows.push({ d: cum, a: a == null || isNaN(a) ? null : a });
    }
    if (!hasAlt) return null;
    // Fill missing altitudes by linear interpolation between known values.
    const filled = [...rows];
    for (let i = 0; i < filled.length; i++) {
      if (filled[i].a == null) {
        let prev = i - 1;
        while (prev >= 0 && filled[prev].a == null) prev--;
        let next = i + 1;
        while (next < filled.length && filled[next].a == null) next++;
        if (prev >= 0 && next < filled.length) {
          const t = (filled[i].d - filled[prev].d) / Math.max(1, filled[next].d - filled[prev].d);
          filled[i].a = filled[prev].a + (filled[next].a - filled[prev].a) * t;
        } else if (prev >= 0) filled[i].a = filled[prev].a;
        else if (next < filled.length) filled[i].a = filled[next].a;
        else filled[i].a = 0;
      }
    }
    const alts = filled.map((r) => r.a);
    const dists = filled.map((r) => r.d);
    const minA = Math.min(...alts);
    const maxA = Math.max(...alts);
    // Elevation gain: sum of positive deltas
    let gain = 0, loss = 0;
    for (let i = 1; i < alts.length; i++) {
      const d = alts[i] - alts[i - 1];
      if (d > 0) gain += d; else loss -= d;
    }
    return { rows: filled, minA, maxA, minD: 0, maxD: dists[dists.length - 1], gain, loss };
  }, [points]);

  if (!data) {
    return (
      <div className="glass-panel rounded-xl px-4 py-3 text-xs text-zinc-500" data-testid="elevation-empty">
        No elevation data recorded for this trek.
      </div>
    );
  }

  const W = 600;
  const H = height;
  const padL = 32, padR = 8, padT = 10, padB = 22;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const spanA = Math.max(1, data.maxA - data.minA);
  const spanD = Math.max(1, data.maxD - data.minD);
  const toX = (d) => padL + ((d - data.minD) / spanD) * innerW;
  const toY = (a) => padT + (1 - (a - data.minA) / spanA) * innerH;

  const linePts = data.rows.map((r) => `${toX(r.d).toFixed(1)},${toY(r.a).toFixed(1)}`).join(' ');
  const areaPts = `${padL},${padT + innerH} ${linePts} ${padL + innerW},${padT + innerH}`;

  // Y-axis ticks
  const ticks = 3;
  const yTicks = Array.from({ length: ticks + 1 }, (_, i) => data.minA + (spanA * i) / ticks);

  return (
    <div className="glass-panel rounded-xl p-3" data-testid="elevation-chart">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Elevation Profile</div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="text-emerald-400 font-mono-stat" data-testid="elev-gain">↑ {Math.round(data.gain)} m</span>
          <span className="text-zinc-400 font-mono-stat" data-testid="elev-loss">↓ {Math.round(data.loss)} m</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label="Elevation profile chart">
        <defs>
          <linearGradient id="elevFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FF9F1C" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#FF9F1C" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Y grid */}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={padL} x2={padL + innerW} y1={toY(v)} y2={toY(v)} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            <text x={padL - 4} y={toY(v) + 3} textAnchor="end" fill="#71717A" fontSize="9" fontFamily="JetBrains Mono, monospace">{Math.round(v)}</text>
          </g>
        ))}
        <polygon points={areaPts} fill="url(#elevFill)" />
        <polyline points={linePts} fill="none" stroke="#FF9F1C" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {/* X labels */}
        <text x={padL} y={H - 6} fill="#71717A" fontSize="9" fontFamily="JetBrains Mono, monospace">0</text>
        <text x={padL + innerW} y={H - 6} textAnchor="end" fill="#71717A" fontSize="9" fontFamily="JetBrains Mono, monospace">{fmtDistance(data.maxD)}</text>
      </svg>
      <div className="mt-1 flex items-center justify-between text-[10px] text-zinc-500 font-mono-stat">
        <span>MIN {Math.round(data.minA)} m</span>
        <span>MAX {Math.round(data.maxA)} m</span>
      </div>
    </div>
  );
}
